import React from 'react';
import { ShieldCheck, ArrowRight, CheckCircle } from 'lucide-react';

export default function Hero() {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 text-white">
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 right-0 bottom-0" 
               style={{
                 backgroundImage: `radial-gradient(circle at center, rgba(255,255,255,0.05) 1px, transparent 1px)`,
                 backgroundSize: '20px 20px'
               }}
          />
        </div>
      </div>
      <div className="absolute -top-24 -right-20 w-96 h-96 bg-emerald-400 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob" />
      <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-400 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob animation-delay-2000" />
      
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-10"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mixBlendMode: 'overlay'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left column */}
          <div className="space-y-8">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium">
              <ShieldCheck className="h-5 w-5 mr-2" />
              Enterprise-grade privacy assessments
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold font-montserrat leading-tight">
              Streamline Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 to-teal-200">
                Privacy Compliance
              </span>{' '}
              
            </h1>
            
            <p className="text-xl text-emerald-50">
              Professional privacy risk assessments utilizing AI and tailored for small and midsize businesses, 
              delivered through our innovative online platform.
              <div className="space-y-2 mt-4 text-base">
                <span className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-emerald-300 mr-3 flex-shrink-0" />
                  Discover unmitigated risks your business is exposed to that can result in financial penalties
                </span>
                <span className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-emerald-300 mr-3 flex-shrink-0" />
                  Acquire documents that demonstrate due diligence and serve as a roadmap for your future privacy compliance efforts
                </span>
              </div>
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#assessments"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-emerald-50 transition-colors group"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/learn-more"
                className="inline-flex items-center justify-center px-6 py-3 border border-white/30 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Learn More
              </a>
            </div>
          </div>
          
          {/* Right column */}
          <div className="lg:pl-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-xl font-semibold mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                {[
                  'Identify your current privacy risk exposure',
                  'AI and expert analysis with actionable insights',
                  'On demand end-to-end assessment solution',
                  'Significant cost savings over traditional methods',
                  'Privacy knowledge or training is not required',
                  'No software to purchase or new platform to learn',
                  'Ability to target compliance for high-risk activities',
                  'We specialize in privacy & data protection compliance'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-emerald-300 mr-3 flex-shrink-0" />
                    <span className="text-emerald-50">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}